import { RefObject, useEffect, useRef } from "react";

const USER_SCROLL_IDLE_MS = 200;

/** True while the user scrolls the listbox (wheel/touch/scrollbar), to avoid fighting react-window. */
export const useListboxUserScroll = ({
  outerElement,
  resetKey,
  onUserScrollEnd,
}: {
  outerElement: HTMLDivElement | null;
  resetKey: unknown;
  onUserScrollEnd?: () => void;
}): RefObject<boolean> => {
  const isUserScrollingRef = useRef(false);

  useEffect(() => {
    if (!outerElement) {
      return undefined;
    }

    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const markUserScroll = (): void => {
      isUserScrollingRef.current = true;
      if (idleTimer !== undefined) {
        clearTimeout(idleTimer);
      }
      idleTimer = setTimeout(() => {
        isUserScrollingRef.current = false;
        onUserScrollEnd?.();
      }, USER_SCROLL_IDLE_MS);
    };

    outerElement.addEventListener("wheel", markUserScroll, { passive: true });
    outerElement.addEventListener("touchmove", markUserScroll, {
      passive: true,
    });
    outerElement.addEventListener("scroll", markUserScroll, { passive: true });

    return () => {
      if (idleTimer !== undefined) {
        clearTimeout(idleTimer);
      }
      outerElement.removeEventListener("wheel", markUserScroll);
      outerElement.removeEventListener("touchmove", markUserScroll);
      outerElement.removeEventListener("scroll", markUserScroll);
    };
  }, [outerElement, onUserScrollEnd, resetKey]);

  return isUserScrollingRef;
};
